import {
  getCustomDataDescriptionsForStep,
  getCustomInputMappingContextId,
  getDataDescriptionsForStep,
  getInputMappingContextForStep,
  getOutputMappingContextForStep,
  getSubDataDescriptionsForStep,
  getSubDataDescriptionsForStepPaths,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/FlowEditService';
import { DataDescriptionType } from '@decisions/server/DecisionsFramework/Utilities/Data';
import create, { UseBoundStore, StoreApi } from 'zustand';
import {
  getNestedDataDescMap,
  getVisibleFlowDataNodes,
  isRowDirectlyHovered,
  isRowHovered,
  filterFlowDataNodes,
  DATA_PATH_SEPARATOR,
} from './utils/MappingUtils';
import { getNestedDataDescByPath, isConstantDataDescription } from './utils/DataDescriptionUtils';
import produce from 'immer';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import React from 'react';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { DataDescription } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { InputMappingConstants, OutputMappingConstants } from 'components/property-editors/MappingConstants';
import { findContextChildByPath } from 'components/property-editors/utils/ProviderNodeUtils';
import {
  getMappingName,
  getMappingPath,
  getPickPath,
} from 'components/property-editors/MappingTypeSelector/helpers/MappingSelectorHelpers';
import { setValue } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { newDataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData';
import { NodeValidationIssues } from 'utils/ValidationUtils';
import { MAPPING_ROW_HEIGHT } from './MappingConnectors/MappingConnectorUtils';
import { GetState, SetState } from 'types/zustand';

/* Store now defines the state for the Mapping Editor (whatever it doesn't share with PropertyGrid) */

export enum MappingPanelPosition {
  LEFT,
  RIGHT,
}

export enum MappingPanelType {
  FLOW,
  STEP,
}

export enum MappingPanelFilter {
  ALL,
  MAPPED,
  UNMAPPED,
}

/** A nested structure for rendering data descriptions */
export interface NestedDataDesc extends DataDescription {
  children: Map<string, NestedDataDesc>;
  filtered?: boolean;
}

export interface IMappingEditorColumnState {
  title: string;
  scrollPosition: number;
  search: string;
  filter: MappingPanelFilter;
  /** Used for constants and to derive selection of mapped node in other column */
  selectedNodeId: string; // TODO should this still be an array? why?
  /** Used for constants and to derive selection of mapped node in other column */
  hoveredNodeId: string;
  expandedNodes: Map<string, boolean>;
  isLoading?: boolean;
  /** if nodes expanded due to search are different than those shown by search */
  searchedExpandedNodes: Map<string, boolean>;
  rowHeights: IMappingRowDimensions;
}

/**
 * exported only for testing
 */
export const CONSTANT_MAPPING_PREFERENCE = 'dp-user-pref-for-constant-mappings';

export type IMappingRowDimensions = Map<string, { top: number; height: number }>;

export interface IDragNewMappingState {
  side: MappingPanelType;
  // node?: ContextProviderNode | DataDescription; // need?
  path: string;
}
export interface IMapping {
  startIndex: number;
  endIndex: number;
  /** element id for starting node */
  startId: string;
  /** element id for ending node */
  endId: string;
  dataPath: string;
  nodeName: string;
  /** right side hidden by search or filter */
  rightHidden?: boolean;
  /** left side hidden by search or filter */
  leftHidden?: boolean;
}

// manually maintaining this interface is a pain... could we get the type some other way?
export interface IMappingEditorStore {
  editingContextId: string;
  visibleFlowDataNodes: string[];
  filteredFlowDataNodes: string[];
  mappedDataPaths: string[];
  flowSessionId: string;
  flowStepId: string;
  forInput: boolean;
  forCustom: boolean;
  flowData: Map<string, NestedDataDesc>;
  // editingContextId: string; // state or props?
  flowPanel: IMappingEditorColumnState;
  stepPanel: IMappingEditorColumnState;
  validation: NodeValidationIssues; // global validation issues - do they have?
  draggingFrom?: IDragNewMappingState;
  dragHasStarted?: boolean;
  mappings: IMapping[];
  dragTargetRowHeights: number[];
  alwaysShowEditors: boolean;
  setScrollPosition: (pos: number, side: MappingPanelType) => void;
  initFlowData: () => Promise<void>;
  getFlowScrollPosition: typeof getFlowScrollPosition;
  getDataDescriptions: typeof getDataDescriptions;
  expandFlowNode: ReturnType<typeof createExpandFlowNode>;
  expandStepNode: (path: string, expand?: boolean) => void;
  setSearchExpandedNodes: (nodes: IMappingEditorColumnState['searchedExpandedNodes'], side: MappingPanelType) => void;
  flowScrollPosition: number;
  hoverNode: (path: string, side: MappingPanelType) => void;
  selectNode: (path: string, side: MappingPanelType) => void;
  hoverClear: () => void;
  selectClear: () => void;
  setFilter: (filter: MappingPanelFilter, side: MappingPanelType) => void;
  setSearch: (search: string, side: MappingPanelType) => void;
  startDragConnector: (path: string, side: MappingPanelType) => void;
  setDragHasStarted: () => void;
  stopDragConnector: () => void;
  setStepRowHeights: (rowHeights: IMappingRowDimensions) => void;
  setDragTargetRowHeights: (side: MappingPanelType) => void;
  initEditingContextId: () => void;
  dropConnector: (snapShotRef?: ContextProviderNode) => Promise<void>;
  clearMapping: () => Promise<void>;
  setDerivedMappings: (mappings: IMapping[]) => void;
  setMappedFlowDataPaths: (mappedFlowDataPaths: string[]) => void;
  toggleAlwaysShowEditors: () => void;
  setIsNodesLoading: (isLoading: boolean) => void;
}

export interface IMappingEditorSelector<U> {
  (selector: (state: IMappingEditorStore) => U, equalityFn?: (a: U, b: U) => boolean): U;
}

// these types are only necessary as we have a factory for one of the action functions below
type MappingEditorStoreSet = SetState<IMappingEditorStore>;
type MappingEditorStoreGet = GetState<IMappingEditorStore>;

//#region store definition

const MappingEditorStoreMap = new Map<string, UseBoundStore<StoreApi<IMappingEditorStore>>>();

const getStoreId = (flowSessionId: string, flowStepId: string, forInput = true) =>
  `${flowSessionId}_${flowStepId}_${forInput ? 'input' : 'output'}`;

/** getter if we need to access from outside React */
export function getMappingEditorStore(flowSessionId: string, flowStepId: string, forInput = true) {
  return MappingEditorStoreMap.get(getStoreId(flowSessionId, flowStepId, forInput));
}

function unCacheStore(flowSessionId: string, flowStepId: string, forInput = true) {
  MappingEditorStoreMap.delete(getStoreId(flowSessionId, flowStepId, forInput));
}

export function createMappingEditorStore(
  flowSessionId: string,
  flowStepId: string,
  forInput: boolean,
  forCustom = false
) {
  const useStore = create<IMappingEditorStore>((set, get) => ({
    editingContextId: '',
    flowData: new Map(),
    flowPanel: {
      title: 'Flow Data',
      scrollPosition: 0,
      filter: MappingPanelFilter.ALL,
      search: '',
      selectedNodeId: '',
      hoveredNodeId: '',
      expandedNodes: new Map(),
      searchedExpandedNodes: new Map(),
      rowHeights: new Map(), // not (yet) used
    },
    stepPanel: {
      title: forInput ? 'Step Inputs' : 'Step Outputs',
      scrollPosition: 0,
      filter: MappingPanelFilter.ALL,
      search: '',
      selectedNodeId: '',
      hoveredNodeId: '',
      expandedNodes: new Map(),
      searchedExpandedNodes: new Map(),
      rowHeights: new Map(),
    },
    validation: {
      issues: [],
      fatal: 0,
      warning: 0,
    },
    dragTargetRowHeights: [],
    alwaysShowEditors: getMappingEditorConstExpanded(),
    visibleFlowDataNodes: [],
    filteredFlowDataNodes: [],
    mappedDataPaths: [],
    forInput,
    flowSessionId,
    flowStepId,
    flowScrollPosition: 0,
    mappings: [],
    forCustom,

    setScrollPosition: (scrollPosition: number, side: MappingPanelType) =>
      set(
        produce<IMappingEditorStore>(state => {
          // immer.produce to
          if (side === MappingPanelType.FLOW) {
            state.flowPanel.scrollPosition = scrollPosition;
          } else {
            state.stepPanel.scrollPosition = scrollPosition;
          }
        })
      ),

    getFlowScrollPosition,

    getDataDescriptions,

    expandFlowNode: createExpandFlowNode(get, set),

    expandStepNode: (path: string, expand?: boolean) => {
      set(
        produce(state => {
          state.stepPanel = expandOrCollapseNode(state.stepPanel, path, expand, getStepSearch(state));
        })
      );
    },

    setSearchExpandedNodes: (nodes: IMappingEditorColumnState['searchedExpandedNodes'], side: MappingPanelType) => {
      const sideKey: 'flowPanel' | 'stepPanel' = side === MappingPanelType.FLOW ? 'flowPanel' : 'stepPanel';
      produce<IMappingEditorStore>(state => {
        state[sideKey].searchedExpandedNodes = nodes;
      });
    },

    initFlowData: async () => {
      const { flowSessionId, flowStepId } = get();
      // realistically we'd also want to fetch all the dependent states here, too, and set for each
      set(
        produce<IMappingEditorStore>(state => {
          state.flowPanel.isLoading = true;
        })
      );
      const constantDataDesc: DataDescription[] = [];
      const dataDesc = forCustom
        ? await getCustomDataDescriptionsForStep({
            descriptionType: forInput ? DataDescriptionType.Read : DataDescriptionType.Write,
            flowSessionId,
            flowStepId,
          })
        : await getDataDescriptionsForStep({
            descriptionType: forInput ? DataDescriptionType.Read : DataDescriptionType.Write,
            flowSessionId,
            flowStepId,
          });

      set(
        produce<IMappingEditorStore>(state => {
          state.flowPanel.isLoading = false;
        })
      );
      // sort special things to the bottom:
      const copyData = [...dataDesc];
      dataDesc.forEach(data => {
        // TODO, I bet we could use .sort for this with the right comparator
        if (isConstantDataDescription(data)) {
          constantDataDesc.push(data);
          const dataIndex = copyData.indexOf(data);
          copyData.splice(dataIndex, 1);
        }
      });
      const sortedDataDesc = copyData.concat(constantDataDesc);
      const flowData = getNestedDataDescMap(sortedDataDesc);
      const visibleFlowDataNodes = getVisibleFlowDataNodes(flowData, getFlowExpandedNodes(get()));
      set({ flowData, visibleFlowDataNodes });

      await getSubDataDescriptionsForPrimaryNodes(get, set);
    },

    hoverNode: (path: string, side: MappingPanelType) =>
      set(
        produce<IMappingEditorStore>(state => {
          if (side === MappingPanelType.FLOW) {
            state.flowPanel.hoveredNodeId = path;
          } else {
            state.stepPanel.hoveredNodeId = path;
          }
        })
      ),

    selectNode: (selectedNodeId: string, side: MappingPanelType) =>
      set(
        produce<IMappingEditorStore>(state => {
          if (side === MappingPanelType.FLOW) {
            if (state.flowPanel.selectedNodeId === selectedNodeId) {
              /** Already selected. Deselect it */
              state.flowPanel.selectedNodeId = '';
              return;
            }
            state.flowPanel.selectedNodeId = selectedNodeId;
            state.stepPanel.selectedNodeId = '';
          } else {
            if (state.stepPanel.selectedNodeId === selectedNodeId) {
              /** Already selected. Deselect it */
              state.stepPanel.selectedNodeId = '';
              return;
            }
            state.stepPanel.selectedNodeId = selectedNodeId;
            state.flowPanel.selectedNodeId = '';
          }
        })
      ),

    hoverClear: () =>
      set(
        produce<IMappingEditorStore>(state => {
          state.flowPanel.hoveredNodeId = '';
          state.stepPanel.hoveredNodeId = '';
        })
      ),

    selectClear: () =>
      set(
        produce<IMappingEditorStore>(state => {
          state.flowPanel.selectedNodeId = '';
          state.stepPanel.selectedNodeId = '';
        })
      ),

    setFilter: async (filter: MappingPanelFilter, side: MappingPanelType) => {
      set(
        produce<IMappingEditorStore>(state => {
          if (side === MappingPanelType.FLOW) {
            state.flowPanel.filter = filter;
          } else {
            state.stepPanel.filter = filter;
          }
          state.flowPanel.selectedNodeId = '';
          state.stepPanel.selectedNodeId = '';
        })
      );
      if (side === MappingPanelType.FLOW) {
        performFlowDataSearchFilter(get, set);
      }
    },

    setSearch: async (search: string, side: MappingPanelType) => {
      set(
        produce<IMappingEditorStore>(state => {
          if (side === MappingPanelType.FLOW) {
            state.flowPanel.search = search;
          } else {
            state.stepPanel.search = search;
          }
        })
      );
      if (side === MappingPanelType.FLOW) {
        performFlowDataSearchFilter(get, set);
      }
    },

    startDragConnector: (path: string, side: MappingPanelType) => {
      get().setDragTargetRowHeights(side);

      set({
        draggingFrom: {
          path,
          side,
        },
      });
    },
    setDragHasStarted: () =>
      set(
        produce<IMappingEditorStore>(state => {
          if (state.draggingFrom) {
            state.dragHasStarted = true;
          }
        })
      ),

    stopDragConnector: () =>
      set(
        produce<IMappingEditorStore>(state => {
          // drop also eliminates this piece of state, but is mostly handled in the API layer.
          state.draggingFrom = undefined;
          state.dragHasStarted = false;
          return state;
        })
      ),

    /**
     * Might seem overkill to index these all at once every time, but so far it's the most reliable way
     * to avoid cases where connectors render and read heights from DOM before rows themselves have finished rendering & painting.
     * @param rowHeights to set
     */
    setStepRowHeights: (rowHeights: IMappingRowDimensions) => {
      set(
        produce<IMappingEditorStore>(state => {
          state.stepPanel.rowHeights = rowHeights;
        })
      );
    },

    setDragTargetRowHeights: (side: MappingPanelType) => {
      if (side === MappingPanelType.FLOW) {
        set({ dragTargetRowHeights: Array.from(get().stepPanel.rowHeights.values()).map(v => v.height) });
      } else {
        set({ dragTargetRowHeights: visibleFlowNodesSelector(get()).map(n => MAPPING_ROW_HEIGHT) });
      }
    },

    initEditingContextId: async () => {
      const { flowSessionId, flowStepId, forInput } = get();
      const editingContextId = forCustom
        ? await getCustomInputMappingContextId({ flowSessionId, flowStepId })
        : forInput
        ? await getInputMappingContextForStep({ flowSessionId, flowStepId })
        : await getOutputMappingContextForStep({ flowSessionId, flowStepId });
      set({ editingContextId });
    },
    setIsNodesLoading: (isLoading: boolean) =>
      set(
        produce<IMappingEditorStore>(state => {
          state.stepPanel.isLoading = isLoading;
          return state;
        })
      ),
    dropConnector: async (snapShotRef?: ContextProviderNode) => {
      const {
        draggingFrom,
        dragHasStarted,
        flowPanel,
        stepPanel,
        editingContextId: contextProviderRegistrationId,
        stopDragConnector,
        selectClear,
      } = get();

      const targetId = draggingFrom?.side === MappingPanelType.FLOW ? stepPanel.hoveredNodeId : flowPanel.hoveredNodeId;

      if (!dragHasStarted || !targetId) {
        // drag has started differentiates row mouse-downs for other reasons from dragging that
        // went into the connector area, so we need that, and  a target id, or else bail out.
        stopDragConnector();
        return;
      }

      const nodePath =
        draggingFrom?.side === MappingPanelType.STEP
          ? draggingFrom.path.split(',')
          : stepPanel.hoveredNodeId.split(',');

      const mappingPath = getMappingPath(nodePath);
      const valuePath = getPickPath(nodePath);

      // value will come from drag source or drop target, depending on the direction being dragged
      const value = draggingFrom?.side === MappingPanelType.FLOW ? draggingFrom.path : flowPanel.hoveredNodeId;

      const targetMappingType = forInput ? InputMappingConstants.MAPPING_SELECT : OutputMappingConstants.MAPPING_CHANGE;
      if (!snapShotRef) {
        // seems impossible, but just in case something happens during loading:
        return stopDragConnector();
      }

      const { child } = findContextChildByPath(nodePath, snapShotRef);
      if (!child) {
        // if we somehow didn't have a target to map to, what are we even doing here?
        return stopDragConnector();
      }

      const currentMapping = getMappingName(child);
      if (currentMapping !== targetMappingType) {
        // set the mapping type:
        try {
          await setValue({
            contextProviderRegistrationId,
            path: mappingPath,
            value: newDataPair({
              Name: 'value',
              OutputValue: targetMappingType,
            }),
          });
        } catch (error) {
          console.error(error);
          stopDragConnector();
          selectClear();
        }
      }

      try {
        await setValue({
          contextProviderRegistrationId,
          path: valuePath,
          value: newDataPair({
            Name: 'value',
            OutputValue: value,
          }),
        });
      } catch (error) {
        console.error(error);
      } finally {
        stopDragConnector();
        selectClear();
      }

      // ContextProviderNodeUpdateEvents should come in and update node tree when above calls are done.
    },

    clearMapping: async () => {
      // this is for connector trash-can clicks, etc.
      const { stepPanel, hoverClear, editingContextId: contextProviderRegistrationId } = get();

      const breakingPath = stepPanel.hoveredNodeId.split(',');

      // TODO do we have getters for these already?
      const breakMappingPath = [...breakingPath, `${breakingPath[breakingPath.length - 1]}__mapping`];

      //Clearing Hover on mapping clear
      hoverClear();

      await setValue({
        contextProviderRegistrationId,
        path: breakMappingPath,
        value: newDataPair({
          Name: 'value',
          OutputValue: InputMappingConstants.MAPPING_UNKNOWN,
        }),
      });
      // no further actions here. ContextProviderNodeUpdateEvent will take care of things.
    },

    setDerivedMappings: (mappings: IMapping[]) => set({ mappings }),

    setMappedFlowDataPaths: (mappedDataPaths: string[]) => {
      const { mappedDataPaths: oldMappedDataPaths } = get();
      if (mappedDataPaths !== oldMappedDataPaths) {
        set({ mappedDataPaths });
        //Run the search
        performFlowDataSearchFilter(get, set);
      }
    },

    toggleAlwaysShowEditors: () => {
      const alwaysShowEditors = !get().alwaysShowEditors;
      set({ alwaysShowEditors });
      // keep in local storage so we remember their choice on reload:
      localStorage.setItem(CONSTANT_MAPPING_PREFERENCE, alwaysShowEditors ? 'y' : '');
    },
  }));

  MappingEditorStoreMap.set(getStoreId(flowSessionId, flowStepId, forInput), useStore);

  return useStore;
}

//#endregion

//#region selector functions

// selector functions could just as easily be inline, but having them pre-defined can DRY up
// reuse, and make them easier to test if they had a complex calculation.
// They also shield components from the effects of store-object adjustments.

export function getFlowScrollPosition(state: IMappingEditorStore) {
  return state.flowPanel.scrollPosition;
}

export function getStepScrollPosition(state: IMappingEditorStore) {
  return state.stepPanel.scrollPosition;
}

export function getLeftScrollPosition(state: IMappingEditorStore) {
  return state.forInput ? getFlowScrollPosition(state) : getStepScrollPosition(state);
}

export function getRightScrollPosition(state: IMappingEditorStore) {
  return state.forInput ? getStepScrollPosition(state) : getFlowScrollPosition(state);
}

export function getStepFilter(state: IMappingEditorStore) {
  return state.stepPanel.filter;
}

export function getFlowFilter(state: IMappingEditorStore) {
  return state.flowPanel.filter;
}

export function getStepSearch(state: IMappingEditorStore) {
  return state.stepPanel.search;
}

export function getFlowSearch(state: IMappingEditorStore) {
  return state.flowPanel.search;
}

export function getStepTitle(state: IMappingEditorStore) {
  return state.stepPanel.title;
}

export function getFlowTitle(state: IMappingEditorStore) {
  return state.flowPanel.title;
}

export function getForInput(state: IMappingEditorStore) {
  return state.forInput;
}

export function getStepPanelPosition(state: IMappingEditorStore) {
  return state.forInput ? MappingPanelPosition.RIGHT : MappingPanelPosition.LEFT;
}

export function getFlowPanelPosition(state: IMappingEditorStore) {
  return state.forInput ? MappingPanelPosition.LEFT : MappingPanelPosition.RIGHT;
}

export function getDataDescriptions(state: IMappingEditorStore) {
  return state.flowData;
}

export function getSelectedFlowNodeId(state: IMappingEditorStore) {
  return state.flowPanel.selectedNodeId;
}

export function getSelectedStepNodeId(state: IMappingEditorStore) {
  return state.stepPanel.selectedNodeId;
}

export function getFlowExpandedNodes(state: IMappingEditorStore) {
  return state.flowPanel.search && state.flowPanel.searchedExpandedNodes
    ? state.flowPanel.searchedExpandedNodes
    : state.flowPanel.expandedNodes;
}

export function getExpandedStepNodes(state: IMappingEditorStore) {
  return state.stepPanel.search && state.stepPanel.searchedExpandedNodes
    ? state.stepPanel.searchedExpandedNodes
    : state.stepPanel.expandedNodes;
}

export function getFlowHoveredNode(state: IMappingEditorStore) {
  return state.flowPanel.hoveredNodeId;
}

export function getStepHoveredNode(state: IMappingEditorStore) {
  return state.stepPanel.hoveredNodeId;
}

export function visibleFlowNodesSelector(state: IMappingEditorStore) {
  return state.visibleFlowDataNodes;
}

export function filteredFlowNodesSelector(state: IMappingEditorStore) {
  return state.filteredFlowDataNodes;
}

export function getEditingContextId(state: IMappingEditorStore) {
  return state.editingContextId;
}

export function getDraggingFrom(state: IMappingEditorStore) {
  return state.draggingFrom;
}

export function getDragTrulyStarted(state: IMappingEditorStore) {
  return state.dragHasStarted;
}

export function draggingFromFlowPath(state: IMappingEditorStore) {
  const draggingFrom = getDraggingFrom(state);
  return draggingFrom?.side === MappingPanelType.FLOW ? draggingFrom.path : '';
}

export function draggingFromStepPath(state: IMappingEditorStore) {
  const draggingFrom = getDraggingFrom(state);
  return draggingFrom?.side === MappingPanelType.STEP ? draggingFrom.path : '';
}

export function storeIdSelector(state: IMappingEditorStore) {
  return getStoreId(state.flowSessionId, state.flowStepId, state.forInput);
}

export function getBothFiltersUnmapped(state: IMappingEditorStore) {
  return (
    state.flowPanel.filter === MappingPanelFilter.UNMAPPED && state.stepPanel.filter === MappingPanelFilter.UNMAPPED
  );
}

export function getDataDescriptionType(state: IMappingEditorStore) {
  return state.forInput ? DataDescriptionType.Read : DataDescriptionType.Write;
}

export function getEndRowHeights(state: IMappingEditorStore) {
  return state.forInput ? state.stepPanel.rowHeights : state.stepPanel.rowHeights;
}

export function getDragTargetRowHeights(state: IMappingEditorStore) {
  return state.dragTargetRowHeights;
}

//#endregion

//#region hooks
// hooks help DRY up boilerplate around getting certain references, e.g.

/** context to pass particular store instance to react children */
export const MappingEditorStore = React.createContext<UseBoundStore<StoreApi<IMappingEditorStore>> | undefined>(
  undefined
);

/**
 * Access particular property of particular store via react context.
 * Must have MappingEditorStore.Provider upstream to use this.
 */
export function useMappingEditorStore<U>(
  selector: (state: IMappingEditorStore) => U,
  equalityFn?: (a: U, b: U) => boolean
): U {
  const useStore = useContext(MappingEditorStore);
  if (!useStore) {
    throw new Error('Mapping Editor store must be configured!');
  }

  return useStore(selector, equalityFn);
}

/**
 * @param flowSessionId id of current flow edit session
 * @param flowStepId current step id
 * @returns existing useStore hook or a new one.
 */
export function useCreateMappingEditorStore(
  flowSessionId: string,
  flowStepId: string,
  forInput: boolean,
  forCustom = false
) {
  // should create a new store if one of these changes, otherwise return original:
  const useStore = useMemo(() => {
    const store = createMappingEditorStore(flowSessionId, flowStepId, forInput, forCustom);

    // initialize things we know we'll need to initialize
    // could collapse these, but it's less of a loading waterfall to kick them off like this
    store.getState().setIsNodesLoading(true);
    store.getState().initFlowData();
    store.getState().initEditingContextId();

    return store;
  }, [flowSessionId, flowStepId, forInput, forCustom]);

  useEffect(() => {
    return () => {
      // console.log('un-mounting MappingEditor store', flowStepId, forInput);
      unCacheStore(flowSessionId, flowStepId, forInput);
    };
  }, [flowSessionId, flowStepId, forInput]);

  return useStore;
}

export function useRowIsHovered(path: string, forFlow = true) {
  return useMappingEditorStore(
    useCallback(state => isRowDirectlyHovered(getFlowHoveredNode(state), getStepHoveredNode(state), path, forFlow), [
      forFlow,
      path,
    ]),
    // custom compare seems necessary, though it shouldn't be in this case...
    (prev, next) => prev === next
  );
}

export function useRowPartnerIsHovered(path: string, forFlow = true) {
  return useMappingEditorStore(
    useCallback(
      // using isRowHovered for faster resolution if row is hovered directly, since they are both used for same styling
      state => isRowHovered(state.mappings, getFlowHoveredNode(state), getStepHoveredNode(state), path, forFlow),
      [forFlow, path]
    ),
    // custom compare seems necessary, though it shouldn't be in this case...
    (prev, next) => prev === next
  );
}

//#endregion hooks

//#region action creator functions

// if we want setter defs not to be inline, we can use factory functions:
function createExpandFlowNode(get: MappingEditorStoreGet, set: MappingEditorStoreSet) {
  // advantage here might be testability, if we mock `set` in tests, we don't have to mock the whole store
  return async (path: string) => {
    // fetch it if it's never been fetched before
    const { flowSessionId, flowStepId, flowPanel, forInput, mappedDataPaths, forCustom } = get();
    const flowSearch = getFlowSearch(get());

    if (flowPanel.expandedNodes.get(path) === undefined) {
      const nestedDataDesc = forCustom
        ? await getCustomDataDescriptionsForStep({
            flowSessionId,
            flowStepId,
            descriptionType: forInput ? DataDescriptionType.Read : DataDescriptionType.Write,
            path,
          })
        : await getSubDataDescriptionsForStep({
            flowSessionId,
            flowStepId,
            descriptionType: forInput ? DataDescriptionType.Read : DataDescriptionType.Write,
            path,
          });

      set(
        produce(state => {
          const children = getNestedDataDescMap(nestedDataDesc, path, flowPanel.filter, mappedDataPaths);
          const nestedParent = getNestedDataDescByPath(state.flowData, path);
          if (nestedParent) {
            nestedParent.children = children;
          }
          children.forEach((value, key, map) => {
            if (value.filtered) {
              let filteredPath = `${path}${DATA_PATH_SEPARATOR}${key}`;
              state.filteredFlowDataNodes.push(filteredPath);
            }
          });
        })
      );
    }

    // toggle the open state
    return set(
      // we might be able to use m  https://github.com/pmndrs/zustand#middleware to bake immer into set
      produce<IMappingEditorStore>(state => {
        const expandedNodes = getFlowExpandedNodes(state);
        const expandingFlowNode = getNestedDataDescByPath(state.flowData, path);
        const expanded = !expandedNodes.get(path);
        // immer updates maps immutably. don't have to copy manually inside produce
        if (flowSearch) {
          state.flowPanel.searchedExpandedNodes.set(path, expanded);
          state.flowPanel.expandedNodes.set(path, false); //prevent re-fetches even of things expanded during fetch
        } else {
          state.flowPanel.expandedNodes.set(path, expanded);
        }
        if (expandingFlowNode) {
          // small anti-pattern, to store derived state of visibleFlowDataNodes, but this lets us control which actions
          // trigger this re-index, vs just trying to be clever with useMemo hooks.
          state.visibleFlowDataNodes = getVisibleFlowDataNodes(state.flowData, expandedNodes);
        }
      })
    );
  };
}

//#endregion

//#region private helpers

function expandOrCollapseNode(panel: IMappingEditorColumnState, path: string, expand?: boolean, search?: string) {
  const key = search ? 'searchedExpandedNodes' : 'expandedNodes';
  // take value if provided:
  expand = typeof expand === 'boolean' ? expand : !panel[key].get(path);
  // set value, immer takes care of immutability for us here:
  panel[key].set(path, expand);
  return panel;
}

function performFlowDataSearchFilter(get: MappingEditorStoreGet, set: MappingEditorStoreSet) {
  const { flowData, flowPanel, mappedDataPaths, filteredFlowDataNodes: oldFilteredFlowDataNodes } = get();
  const flowSearch = flowPanel.search;
  const flowFilter = flowPanel.filter;

  //bail if no search string, filter, and filteredFlowDataNodes is already empty.
  if (!flowSearch && flowFilter === MappingPanelFilter.ALL && oldFilteredFlowDataNodes.length === 0) {
    // why do we need && oldFilteredFlowDataNodes.length === 0 again? maybe ) || oldFilteredFlowDataNodes.length === 0 ?
    return;
  }

  let filteredFlowData = new Map(flowData);
  let expandedNodes = new Map(getFlowExpandedNodes(get()));
  let filteredFlowDataNodes: string[] = [];

  ({
    results: filteredFlowData,
    newExpandedNodes: expandedNodes,
    filteredNodes: filteredFlowDataNodes,
  } = filterFlowDataNodes(flowData, expandedNodes, flowSearch, flowFilter, mappedDataPaths));
  let visibleFlowDataNodes = getVisibleFlowDataNodes(filteredFlowData, expandedNodes);

  set(
    produce<IMappingEditorStore>(state => {
      state.flowData = filteredFlowData;
      state.visibleFlowDataNodes = visibleFlowDataNodes;
      state.filteredFlowDataNodes = filteredFlowDataNodes;
      state.flowPanel.searchedExpandedNodes = new Map(expandedNodes);
    })
  );
}

export function getMappingEditorConstExpanded() {
  return !!localStorage.getItem(CONSTANT_MAPPING_PREFERENCE) || false;
}

async function getSubDataDescriptionsForPrimaryNodes(get: MappingEditorStoreGet, set: MappingEditorStoreSet) {
  const { flowSessionId, flowStepId, forInput, flowData } = get();
  const paths = Array.from(flowData.keys());

  const subDataDescs = await getSubDataDescriptionsForStepPaths({
    flowSessionId,
    flowStepId,
    descriptionType: forInput ? DataDescriptionType.Read : DataDescriptionType.Write,
    paths,
  });

  set(
    produce(state => {
      subDataDescs.forEach(subDataDesc => {
        const path = subDataDesc.Name;
        const childrenData = subDataDesc.OutputValue as DataDescription[];
        const children = getNestedDataDescMap(childrenData);
        const nestedParent = getNestedDataDescByPath(state.flowData, path);

        if (nestedParent) {
          nestedParent.children = children;
        }
        state.flowPanel.expandedNodes.set(path, false);
      });
    })
  );
}
//#endregion
